import { DEFAULT_LOTTERY_TASK_NAME, TaskType, EmojiReplyType, NewLotteryType, PrizeAllocationType, EXCHANGE_OFFICIAL_PRIZE_TYPES, EXCHANGE_OFFICIAL_PRIZE_DURATION, SCREENSHARE_TYPE } from "_lib/constants/bot"
import { LotteryStatus } from "./api"

export type LotteryId = string
export type RoomId = string

export interface LotteryDataFull {
    lottery_id: LotteryId
    creator: number
    created_at: number
    title: string
    descriptions: string[]
    start_at: number
    end_at: number
    draw_at: number
    room_id: RoomId
    prize_image: string
    channel_id: string
    prize_configs?: PrizeConfig[]
    task_configs?: TaskConfig[]
    advanced_config?: AdvancedConfig
    channel_lottery_msg: string
    state: LotteryStatus
    winners: IWinner[],
    participant_count: number,
    task_finished_count: number,
    is_manual: boolean,
    lottery_type: NewLotteryType,
    points_lottery_config?: IPointsLotteryConfig,
    show_prize_count: boolean,
}

export type LotteryDataBody = Omit<LotteryDataFull, 'lottery_id' | 'creator' | 'created_at'>

export enum SectionType {
    Overview = 'Overview',
    WinningList = 'WinningList',
    ParticipantList = 'ParticipantList',
    Prize = 'Prize',
    BlockUser = 'BlockUser',
    Task = 'Task',
    PointsLotteryConfig = 'PointsLotteryConfig',
}
export enum ViewType {
    SignIn = 'SignIn', // 积分获取
    PointExchange = 'PointExchange', // 积分兑换
    AutoReply = 'AutoReply', // 自动回复
    BotLottery = 'BotLottery', // 机器人抽奖
}

// 奖品类型
export enum PrizeType {
    CDKey = 1,
    Physical = 2,
    Points = 3,
    Official = 4,
}

export enum PreviewMessageId {
    lottery = '1',
    notifyChannel = '2',
    notifyPrivate = '3',
    privateIM = '4'
}

export enum TaskIsDaily {
    Once = '0',
    Daily = '1',
}

export class PrizeConfig {
    constructor(
        public prize_name: string = '',
        public prize_type: PrizeType = PrizeType.CDKey,
        public prize_list: string[] = [],
        public prize_item_list: {item_id: string, prize_id: string, value: string}[] = [],
        public count: number = 0,
        public user_notify: string = '',
        public channel_notify: string = '',
        public channel_id: string = '',
        public prize_cover: string = '',
        public prize_cover_height: number = 0,

        // 积分抽奖相关
        public odds: number = 1,
        public lottery_limit: number = 1,
        public distribution: PrizeAllocationType = PrizeAllocationType.NoAverage,
        public risk_control: number = 0,

        // 奖品类型为积分时
        public points_count: number = 1,

        // 官方奖励
        public official_prize_type: EXCHANGE_OFFICIAL_PRIZE_TYPES = EXCHANGE_OFFICIAL_PRIZE_TYPES.SCREENSHARE,
        public official_prize_duration: EXCHANGE_OFFICIAL_PRIZE_DURATION = EXCHANGE_OFFICIAL_PRIZE_DURATION.DAY,
        public official_prize_id: string = '',
        public screen_share_resolution_type: SCREENSHARE_TYPE = SCREENSHARE_TYPE.SCREENSHARE_720,

        // 显示 不可更改
        public can_roll_count: number = -1,
        public used_count: number = 0,
    ) {
        this.channel_notify = this.channelNotifyDefault(channel_notify)
        this.user_notify = this.userNotifyDefault(prize_type, user_notify)
    }
    channelNotifyDefault(channel_notify: string): string {
        return channel_notify || '恭喜在{标题}中有{获奖人数}人获得了{奖品名称}，{@全体获奖成员}'
    }
    
    userNotifyDefault(prize_type: PrizeType = this.prize_type, user_notify: string = ''): string {
        switch (prize_type) {
            case PrizeType.CDKey:
                return user_notify || '恭喜您在{标题}中获得了{奖品名称}，CDK 为 {CDK}'
            case PrizeType.Physical:
                return user_notify || '恭喜您在{标题}中获得了{奖品名称}'
            case PrizeType.Points:
                return user_notify || '恭喜您在{标题}中获得了{奖品名称}，积分数量 为 {积分数量}'
            case PrizeType.Official:
                return user_notify || '恭喜您在{标题}中获得了{奖品名称}'
            default:
                console.error('unsupported prize_type: ', prize_type)
                return user_notify || ''
        }
    }
    static defaultUserNotifyTemplate(prize_type: PrizeType): string {
        switch (prize_type) {
            case PrizeType.CDKey:
                return '恭喜您在{标题}中获得了{奖品名称}，CDK 为 {CDK}'
            case PrizeType.Physical:
                return '恭喜您在{标题}中获得了{奖品名称}'
            case PrizeType.Points:
                return '恭喜您在{标题}中获得了{积分数量}积分'
            case PrizeType.Official:
                return '恭喜您在{标题}中获得了{官方奖品名称}，时长为 {时长}'
            default:
                console.error('unsupported prize_type: ', prize_type)
                return ''
        }
    }
    static defaultChannelNotifyTemplate(lotteryType: NewLotteryType): string {
        if(lotteryType === NewLotteryType.Points) {
            return '恭喜{@中奖成员}在{标题}中抽到了{奖品名称}'
        } else {
            return '恭喜在{标题}中有{获奖人数}人获得了{奖品名称}，{@全体获奖成员}'
        }
    }
}

export class TaskConfig {
    
    constructor(
        public task_id: string = '',
        public task_name: string = DEFAULT_LOTTERY_TASK_NAME[TaskType.Invite],
        public task_type: TaskType = TaskType.Invite,
        public invite_user_count: string = '0',
        public channel_id: string = '',
        public send_msg_content: string = '',
        public chat_user_count: string = '0',
        public chat_time: string = '0',
        public task_config?: Object,
        public push_content: string = '恭喜您已完成 {标题} 的任务：{任务名称}',
        public reply_emoji_type: EmojiReplyType = EmojiReplyType.Current, // 回复表情类型
        public reply_msg_id: string = '', // 回复表情 指定消息id
        public reply_emoji_channel_id: string = '', // 回复表情 指定消息频道id
        public reply_msg_link: string = '', // 回复表情 指定消息链接
        public send_img_channel_id: string = '', // 发送图片 指定消息频道id
        public cumulative_chat_user_count: string = '0', // 累计语音 频道内语音人数
        public cumulative_chat_time: string = '0', // 累计语音 累计语音时长
        public cumulative_chat_day: string = '1', // 累计语音 累计语音天数
        public is_daily: TaskIsDaily = TaskIsDaily.Once, // 是否是每日任务
        public play_game_appid: string = '0', // 玩指定游戏 游戏appid
    ) {}
}

export class AdvancedConfig {
    constructor(
        public block_users: number[] = [],
        public block_users_info: UserInfo[] = [],
        public block_groups: string[] = [],
        public related_lotteries: string[] = [],
        public heybox_level: number | undefined = undefined,
        public active_time: number | undefined = undefined,
    ) {}
}

export interface IChannel {
    channel_name: string,
    channel_id: string,
    channel_type: number,
    is_private: number,
    password: boolean,
}

export interface IWinner {
    nickname: string,
    prize_item: {
        item_id: string,
        prize_id: string,
        value: string,
    },
    prize_value: number,
    user_id: number,
}

export interface RoomRole {
    id: string;
    name: string;
    type: number;
    department_id: string;
    permissions: string;
    color: number;
    icon: string;
    position: number;
    del_tag: number;
    hoist: number;
    mentionable: number;
    color_list: null;
}

export interface IRoom {
    room_id: string;
    room_name: string;
    room_avatar: string;
    room_pic: string;
    created_by: string;
    bot_joined: boolean;
}

export class BotInfo {
    bot_id?: number;
    nickname?: string;
    signature?: string;
    avatar?: string;
    app_Token?: string;
    permissions?: string;
    onlineState?: boolean;
    create_time?: string;
}

export interface UserInfo {
    user_id: number
    nickname: string
    avatar: string
}

export interface IEventBus extends Vue {
    $on(event: string | Array<string>, callback: Function): this;
    $emit(event: string, ...args: any[]): this;
    $off(event?: string | Array<string>, callback?: Function): this;
    $once(event: string, callback: Function): this;
    // 添加你的自定义方法
    $dynamic(name: string, ...args: any[]): void;
  }

export interface IBaseRule {
    rule_type: number,
    days: number,
    points: number,
}

export interface ISignInRule {
    custom_points_name: string,
    base_check_in: {
        is_open: boolean,
        channel_id: string,
        base_points: number,
        rules: IBaseRule[],
    },
    audio_check_in: {
        is_open: boolean,
        duration: string,
        points: string,
        type: string,
        appid: string,
    },
    chat_check_in: {
        is_open: boolean,
        channel_ids: string[],
        points: string,
    },
}

// 玩指定游戏 游戏选项
export interface ITaskGameOption {
    label: string,
    value: string,
    icon: string,
}

/**
 * 积分抽奖条件
 */
export interface IPointsLotteryConfig {
    /**
     * 单次抽奖消耗积分 
     */
    cost_points: string

    /**
     * 未中奖概率
     */
    nonwinner_probability: string

    /**
     * 未中奖通知
     */
    nonwinner_msg: string
}